<template>
    <form
        id="create-other-meeting-form"
        ref="create-other-meeting-form"
        @submit.prevent="saveMeeting"
    >
        <modal v-model="showModal">
            <template #title>{{ trans('apps.team_meetings.title') }}</template>

            <section v-if="showModal">
                <!-- Existing Entries warning -->
                <existing-entries-warning
                    v-if="existingEntries.length && !showRepeatOptions"
                    :existing-entries="existingEntries"
                />

                <!-- Meeting date and time -->
                <div class="row">
                    <div class="col-sm-3 col-xs-6">
                        <div class="form-group" :class="{ 'has-error' : errors.has('start_date') }">
                            <label  class="control-label">{{ trans('global.start') }}</label>
                            <date-picker v-model="meeting.startDate" name="startDate"></date-picker>
                        </div>
                    </div>

                    <div class="col-sm-2 col-xs-4">
                        <div class="form-group" :class="{ 'has-error' : errors.has('start_time') }">
                            <label for="other-start-time" class="control-label">&nbsp;</label>
                            <select
                                id="other-start-time"
                                v-model="meeting.startTime"
                                class="p-5 form-control"
                                name="startTime"
                            >
                                <option
                                    v-for="available_time in available_times"
                                    :key="available_time"
                                    :value="available_time">
                                    {{ available_time }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <div class="col-xs-2" :class="editMode ? 'col-sm-2' : 'col-sm-1'">
                        <p class="m-0 text-center hidden-xs p-t-20 text-muted" style="padding-top:35px;"><i class="fa fa-arrow-right"></i></p>
                    </div>

                    <div class="col-sm-3 col-xs-6">
                        <div class="form-group" :class="{ 'has-error' : errors.has('end_date') }">
                            <label class="control-label">{{ trans('global.end') }}</label>
                            <date-picker
                                v-model="meeting.endDate"
                                name="endDate"
                                :min_date="meeting.startDate"
                            />
                        </div>
                    </div>

                    <div class="col-sm-2 col-xs-4">
                        <div class="form-group" :class="{ 'has-error' : errors.has('end_time') }">
                            <label for="other-end-time" class="control-label">&nbsp;</label>
                            <select
                                id="other-end-time"
                                v-model="meeting.endTime"
                                class="p-5 form-control"
                                name="endTime"
                            >
                                <option
                                    v-for="available_time in available_end_times"
                                    :key="available_time"
                                    :value="available_time">
                                    {{ available_time }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <div v-if="!editMode" class="col-xs-2 col-sm-1">
                        <div class="flex flex-col">
                            <label>&nbsp;</label>
                            <ep-button
                                style="padding: 11px 0"
                                variant="tertiary"
                                start-icon="repeat"
                                :title="trans('calendar.create_client_label_repeat_2')"
                                @click.prevent="showRepeatOptions = !showRepeatOptions"
                            />
                        </div>
                    </div>
                </div>

                <div v-if="!editMode" v-show="showRepeatOptions" class="row">
                    <div class="col-xs-12">
                        <div class="form-group form-inline repeat_options_wrap">
                            {{ trans('calendar.create_client_label_repeat') }}

                            <!-- v-model breaks this element on Safari. Could be fixed in future Safari/Vue releases -->
                            <select
                                id="repeat_interval"
                                v-model="repeatInterval"
                                name="repeat_interval"
                                class="form-control m-l-10 m-r-10 m-b-0"
                            >
                                <option
                                    v-for="option in repeatOptions"
                                    :key="option.value"
                                    :value="option.value"
                                >
                                    {{ trans(option.label) }}
                                </option>
                            </select>

                            {{ trans('calendar.create_client_repeat_untill') }}

                            <div class="form-group" :class="{ 'has-error' : errors.has('until') }">
                                <date-picker
                                    v-model="repeatUntilDate"
                                    name="repeat_date"
                                    :min_date="meeting.startDate"
                                    class="m-l-10 inline-block align-middle"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Meeting title -->
                <div class="row">
                    <div class="col-xs-9 col-sm-10">
                        <div class="form-group" :class="{ 'has-error' : errors.has('title') }">
                            <label for="meeting-title" class="control-label">{{ trans('client.journal_title') }}</label>
                            <input id="meeting-title" v-model="meeting.title" type="text" class="form-control" name="title"  required/>
                            <span v-show="errors.has('title')" class="help-block">{{ errors.first('title') }}</span>
                        </div>
                    </div>
                    <div class="col-xs-3 col-sm-2">
                        <color-picker
                            :stored_color="meeting.color"
                            @changeColor="meeting.color = $event"
                        />
                    </div>
                </div>

                <!-- Calendar Section -->
                <!-- Show this section to everyone except the organizer, because only organizer can change the calendar -->
                <div
                    v-if="meeting.calendarId && editMode && !isOrganizer"
                    class="flex gap-x-4 items-baseline mt-5 mb-8"
                >
                    <i class="fas fa-circle" :style="{color: meeting.calendar.color}"></i>
                    <p class="mb-0">{{ meeting.calendar.name }}</p>
                </div>

                <div v-if="show_calendars_select" class="row">
                    <div class="col-xs-12" >
                        <div class="form-group" :class="{ 'has-error' : errors.has('calendar_id') }">
                            <label for="calendar" class="control-label">
                                {{ trans('calendar.create_client_label_choose_calendar') }}
                                <i class="fa fa-circle m-l-5" :style="'color:' + chosen_calendar.color + ';'"></i>
                            </label>
                            <select v-model="meeting.calendarId" class="form-control" name="calendarId" id="meeting-other-calendar">
                                <option v-for="calendar in calendarsWithEditAccess" :key="calendar.id" :value="calendar.id">
                                    {{ calendar.name }}
                                </option>
                            </select>
                            <span v-show="errors.has('calendar_id')" class="help-block">{{ errors.first('calendar_id') }}</span>
                        </div>
                    </div>
                </div>

                <input v-else type="hidden" name="calendarId" :value="calendarsWithEditAccess[0].id" />
                <!-- End Calendar Section -->

                <!-- Participants -->
                <div class="row">
                    <!-- Select Participants -->
                    <div class="col-xs-12">
                        <div class="form-group" :class="{ 'has-error': errors.has('participants') }">
                            <label for="select_employee" class="control-label">
                                {{ trans('apps.team_meetings.select_participants') }}
                            </label>

                            <select
                                id="select_employee"
                                v-model="selectedParticipant"
                                class="form-control"
                                name="select_employee"
                                @change="addParticipant"
                            >
                                <option :value="null">{{ placeholderText }}</option>
                                <option
                                    v-for="participant in filteredParticipantsOptions"
                                    :key="participant.id"
                                    :value="participant"
                                >
                                    {{ participant.realname }}
                                </option>
                            </select>
                            <span v-show="errors.has('participants')" class="help-block">
                            {{ errors.first('participants') }}
                        </span>
                        </div>
                    </div>

                    <!-- Warning -->
                    <div class="col-xs-12">
                        <ep-banner
                            v-show="hasParticipantsWithoutCalendarAccess"
                            v-sanitize-html="trans('apps.team_meetings.cannot_see_warning', {calendar: chosen_calendar.name })"
                            variant="warning"
                            style="margin-bottom: 1.5rem;"
                        />
                    </div>

                    <!-- Organizer + Participants listing -->
                    <div class="col-xs-12">
                        <div class="flex flex-col mb-7">
                            <!-- Organizer is also the primary Participant -->
                            <participant-details
                                v-if="meeting.organizer"
                                editable
                                :participant="meeting.organizer"
                                is-organizer
                                class="px-5"
                            />

                            <!-- Participants -->
                            <participants-list
                                v-for="participant in meeting.participants"
                                :key="participant.id"
                            >
                                <participant-details
                                    editable
                                    :participant="participant"
                                    :booked-slot="practitionersWithExistingEntries.includes(participant.id)"
                                    :loading="entriesLoading"
                                />
                                <div class="actions flex gap-x-6 items-center">
                                    <!-- Grant access -->
                                    <ep-button
                                        v-if="!participant.canSeeMeeting"
                                        :tooltip="trans('apps.team_meetings.grant_access_tooltip')"
                                        variant="secondary"
                                        size="xs"
                                        :loading="grantAccessLoader && participant.id === grantingAccessParticipantId"
                                        :disabled="grantAccessLoader"
                                        @click="grantCalendarAccess(participant.id)"
                                    >
                                        {{ trans('apps.team_meetings.grant_view_access') }}
                                    </ep-button>
                                    <!-- Hide this normally but show when list item is hovered-->
                                    <i
                                        class="far fa-times cursor-pointer group-hover:inline-block hidden text-md w-6 text-center"
                                        @click="removeParticipant(participant)"
                                    ></i>
                                    <!-- eye cancel icon -->
                                    <i v-show="!participant.canSeeMeeting" class="far fa-eye-slash cursor-pointer group-hover:hidden w-6"></i>
                                </div>
                            </participants-list>
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-xs-12">
                        <div class="form-group">
                            <label for="other-meeting-" class="control-label">{{ trans('calendar.create_client_label_notes') }}</label>
                            <textarea
                                id="other-meeting-notes"
                                v-model="meeting.description"
                                class="form-control"
                                name="description"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <template #footer>
                <ep-button
                    type="submit"
                    variant="primary"
                    :loading="loading"
                    :disabled="loading"
                >
                    {{  editMode ? trans('global.update') : trans('global.add') }}
                </ep-button>
            </template>
        </modal>
    </form>
</template>

<script>
import axios from 'axios';
import swal from 'sweetalert';
import moment from 'moment/moment';
import { http } from '@/system/http';
import { size, debounce } from 'lodash';
import Modal from '@/system/components/global/Modal';
import TimePicker from '@/system/components/mixins/TimePicker';
import Iso8601Duration from '@/system/helpers/Iso8601Duration';
import DatePicker from '@/system/components/global/form/DatePicker';
import ColorPicker from '@/system/components/global/form/ColorPicker';
import ExistingEntries from '@/system/components/mixins/ExistingEntries';
import ParticipantsList from '@/system/components/apps/team_meetings/ParticipantsList';
import ParticipantDetails from '@/system/components/apps/team_meetings/ParticipantDetails';
import ExistingEntriesWarning from '@/system/components/calendar/events/ExistingEntriesWarning';

export default {
    compatConfig: {
        WATCH_ARRAY: false,
    },
    
    components: {
        ExistingEntriesWarning,
        ParticipantsList,
        ParticipantDetails,
        DatePicker,
        ColorPicker,
        Modal
    },

    mixins: [TimePicker, ExistingEntries],

    data() {
        return {
            showModal: false,
            meeting: {
                participants: [], // reactive
            },
            date: '',
            loading: false,
            title: '',
            load_existing_meetings: false,
            selectedParticipant: null,
            grantAccessLoader: false,
            isPreparingForRecurring: true,
            grantingAccessParticipantId: null,
            // properties for recurring feature
            showRepeatOptions: false,
            repeatInterval: 'P1D',
            repeatUntilDate: null,
            applyRecurringActionTo: null,
            repeatOptions: Iso8601Duration,
            cancelTokenSource: null,
            calendarsWithExistingEntries: [],
            entriesLoading: false,
        };
    },

    computed: {
        available_times() {
            const availableTimes = this.get_available_times(this.user.calendarstarttime, this.user.calendarendtime);
            const midnight = '00:00';
            const selectedTimes = [this.meeting.startTime, this.meeting.endTime];

            if (selectedTimes.includes(midnight) && !availableTimes.includes(midnight)) {
                availableTimes.unshift(midnight);
            }

            return availableTimes;
        },

        calendars() {
            return this.$store.getters['calendars/getCalendars']();
        },

        calendarsWithEditAccess() {
            return this.calendars.filter(calendar => calendar.can_edit);
        },

        chosen_calendar() {
            if (this.meeting.calendarId > 0) {
                return this.$store.getters['calendars/getCalendarById'](this.meeting.calendarId);
            }

            return false;
        },

        available_end_times () {
            const availableStartTimes = this.available_times;

            // only go ahead if the end date > start date, if not, just return all the available times
            if (this.isEndDateAfterStartDate) return availableStartTimes;

            // find index of this.meeting.startTime and return the array after that index
            const findIndex = availableStartTimes && availableStartTimes.findIndex(time => time === this.meeting.startTime);
            // remove all the times before the start time
            if (findIndex === availableStartTimes.length - 1) {
                return availableStartTimes.slice(findIndex);
            } else {
                return availableStartTimes.slice(findIndex + 1);
            }
        },

        isEndDateAfterStartDate() {
            const startDate = moment(this.meeting.startDate, 'DD-MM-YYYY');
            const endDate = moment(this.meeting.endDate, 'DD-MM-YYYY');
            return endDate > startDate;
        },

        show_calendars_select() {
            return size(this.calendarsWithEditAccess) !== 1
                && (!this.editMode || this.isOrganizer);
        },

        user() {
            return this.$store.state.auth.user;
        },

        owner() {
            return this.$store.state.auth.owner;
        },

        isOwner() {
            return this.$store.getters['auth/isOwner'];
        },

        isOrganizer() {
            return this.user.id === this.meeting.organizer?.id;
        },

        practitionersList() {
            const allPractitioners = this.$store.getters['practitioners/getPractitioners'];

            if (this.isOwner) {
                return allPractitioners;
            }

            return allPractitioners.filter(practitioner => {
                return this.calendars.some(calendar => {
                    return calendar.practitioner_user?.id === practitioner.id && calendar.can_edit;
                });
            });
        },

        participantOptions () {
            const options = [this.owner, ...this.practitionersList];

            if (this.editMode) {
                return options.filter(option => option.id !== this.meeting.organizer.id);
            }

            return options;
        },

        filteredParticipantsOptions () {
            if (!this.meeting.participants) {
                return this.participantOptions;
            }

            return this.participantOptions.filter(participant => {
                 return !this.meeting.participants.some(selected => selected.id === participant.id) // remove existing participants
                     && (this.editMode || participant.id !== this.user.id) // Add the user to the list if it's an edit and omit if creating
            })},

        placeholderText() {
            return this.filteredParticipantsOptions.length === 0
                ? this.trans('apps.team_meetings.no_more_participants')
                : this.trans('apps.team_meetings.add_participant')
        },

        editMode() {
            return this.meeting.id > 0;
        },

        hasParticipantsWithoutCalendarAccess() {
            return this.meeting?.participants?.some(participant => !participant.canSeeMeeting);
        },

        requestUrl() {
            let url = '/app/team-meetings/meetings';

            if (this.editMode)
                url = `/app/team-meetings/meetings/${this.meeting.id}`;

            if (this.showRepeatOptions)
                url = '/app/team-meetings/meetings/recurring';

            return url;
        },
        canUserEditCalendar() {
            return this.$store.getters['practitioners/canUserEditCalendar'];
        },

        canUserSeeCalendar() {
            return this.$store.getters['practitioners/canUserSeeCalendar'];
        },

        practitionerCalendarMap() {
            // returns an object like: { practitioner_id: calendar_id, ... }
            return this.calendars.reduce((acc, calendar) => {
                if (calendar.practitioner_user) {
                    acc[calendar.practitioner_user.id] = calendar.id;
                }
                return acc;
            }, {});
        },

        practitionersWithExistingEntries() {
            return this.meeting.participants
                .filter(participant => {
                    return this.calendarsWithExistingEntries?.includes(this.practitionerCalendarMap[participant.id]);
                })
                .map(participant => participant.id);
        },
    },

    watch: {
        showModal(val) {
            if (!val) {
                this.resetForm();
            }
        },

        'meeting.participants'(newValue, oldValue) {
            if (newValue.length) {
                this.errors.remove('participants');
                if (newValue.length < oldValue.length) return; // if a participant is removed from the meeting, don't make a request
                this.getCalendarsWithExistingEntries();
            }
        },

        'meeting.calendarId'(val, oldValue) {
            if (val) {
                this.setSelectedParticipants(this.meeting)
                this.getExistingEntriesRequest(val, oldValue);
            }
        },

        showRepeatOptions(val) {
            if (val) {
                this.repeatUntilDate = this.repeatUntilDate
                    ?? moment(this.meeting.startDate, 'DD-MM-YYYY').format('DD-MM-YYYY');
                this.calendarsWithExistingEntries = [];
            } else {
                this.getCalendarsWithExistingEntries();
            }
        },

        'meeting.startDate'(newValue, oldValue) {
            this.getExistingEntriesRequest(newValue, oldValue);
        },

        'meeting.endDate'(newValue, oldValue) {
            this.getExistingEntriesRequest(newValue, oldValue);
        },

        'meeting.startTime'(newValue, oldValue) {
            this.getExistingEntriesRequest(newValue, oldValue);
        },

        'meeting.endTime'(newValue, oldValue) {
            this.getExistingEntriesRequest(newValue, oldValue);
        },
    },

    mounted() {
        this.createMeetingEventHandler();
        this.editMeetingEventHandler();
        this.updateRecurrenceEventHandler();
    },

    methods: {
        createMeetingEventHandler() {
            this.$event_bus.$on('showTeamMeetingFormModal', ({ start, end, calendarId }) => {
                this.showModal = true;

                if (typeof start !== 'undefined' && typeof end !== 'undefined') {
                    this.meeting.startDate = start.format("DD-MM-YYYY")
                    this.meeting.startTime = start.format("HH:mm")
                    this.meeting.endDate = end.format("DD-MM-YYYY")
                    this.meeting.endTime = end.format("HH:mm")
                }

                this.meeting.calendarId = calendarId
                    ? calendarId
                    : this.$store.getters['calendars/getDefaultCalendar']().id;
            });
        },

        editMeetingEventHandler() {
            this.$event_bus.$on('editTeamMeeting', (meeting) => {
                this.showModal = true;
                this.meeting = {
                    id: meeting.id,
                    title: meeting.title,
                    startDate: moment(meeting.start).format('DD-MM-YYYY'),
                    startTime: moment(meeting.start).format('HH:mm'),
                    endDate: moment(meeting.end).format('DD-MM-YYYY'),
                    endTime: moment(meeting.end).format('HH:mm'),
                    organizer: meeting.organizer,
                    calendarId: meeting.calendar.id,
                    calendar: meeting.calendar,
                    description: meeting.description,
                    color: meeting.color,
                    recurring: meeting.recurring,
                    participants: meeting.participants, // these are just ids of the participants
                    additional_calendars: meeting.additional_calendars,
                };

                this.setSelectedParticipants(meeting);
            });
        },

        saveMeeting() {
            if (this.editMode) {
                this.updateMeeting();
                return;
            }

            if (this.showRepeatOptions) {
                this.getRecurringMeetingDates();
                return;
            }

            this.submitMeeting();
        },

        async submitMeeting() {
            this.loading = true;

            const meeting = this.getMeetingRequestObject()

            try {
                const { data } = await http.post(this.requestUrl, meeting);
                toastr.success(data.message);
                this.$event_bus.$emit('calendarReload');
                this.showModal = false;
            } catch ({ errorBag }) {
                this.$setErrorBag(errorBag);
            } finally {
                this.loading = false;
            }
        },

        updateMeeting() {
            if (this.meeting.recurring) {
                this.$event_bus.$emit('showMeetingRecurringModal', this.meeting, 'update');
                return;
            }

            this.updateTeamMeetingRequest();
        },

        async updateTeamMeetingRequest () {
            this.loading = true;

            const meeting = this.getMeetingRequestObject()

            const config = this.applyRecurringActionTo
                ? { params: { action: this.applyRecurringActionTo } }
                : null;

            try {
                const { data } = await http.put(this.requestUrl, meeting, config);

                // show success message
                toastr.success(data.message);

                // emit events
                this.$event_bus.$emit('showTeamMeetingModal', data.team_meeting, true);
                if (config) this.$event_bus.$emit('recurringTeamMeetingAdjusted');

                // close form modal
                this.showModal = false;
            } catch ({ errorBag }) {
                this.$setErrorBag(errorBag);
            } finally {
                this.loading = false;
            }
        },

        updateRecurrenceEventHandler() {
            this.$event_bus.$on('updateRecurringTeamMeetings', action => {
                this.applyRecurringActionTo = action;
                this.updateTeamMeetingRequest();
            });
        },

        async getRecurringMeetingDates() {
            this.loading = true;

            const meeting = this.getMeetingRequestObject()

            try {
                const { data } = await http.post('/app/team-meetings/meetings/recurring/dates', meeting);

                swal({
                    title: this.trans('calendar.create_recurring_label'),
                    text: this.alertMessage(data),
                    showCancelButton: true,
                    html: true,
                    cancelButtonText: this.trans('global.cancel'),
                    showLoaderOnConfirm: true,
                    closeOnConfirm: false,
                }, () => {
                    swal.close();
                    this.submitMeeting()
                });
            } catch ({ errorBag }) {
                this.$setErrorBag(errorBag);
            } finally {
                this.loading = false;
            }
        },

        getMeetingRequestObject () {
            const meeting = {
                title: this.meeting.title,
                start_date: this.meeting.startDate,
                start_time: this.meeting.startTime,
                end_date: this.meeting.endDate,
                end_time: this.meeting.endTime,
                calendar_id: this.meeting.calendarId,
                description: this.meeting.description,
                color: this.meeting.color,
                recurring: this.meeting.recurring,
                participants: this.meeting.participants?.map(participant => participant.id),
            }

            if (this.showRepeatOptions) {
                meeting.until = this.repeatUntilDate
                meeting.recurring = this.repeatInterval
            }

            return meeting;
        },

        addParticipant() {
            if (this.selectedParticipant) {
                const accessGranted = this.setMeetingAccess(this.selectedParticipant);
                this.meeting.participants = [...(this.meeting.participants || []), { ...this.selectedParticipant, canSeeMeeting: accessGranted }];
                this.selectedParticipant = null;
            }
        },

        setMeetingAccess(participant) {
            /**
             * Ignoring these cases for now, where a practitioner has access to:
             * 1. a calendar which is owned by someone else
             * 2. and/or another participant's calendar is also a part of the meeting.
             */

            // if the participant is the owner => true
            if (participant.id === this.owner.id) return true;

            // if the participant has calendars and has any access to it any one of it => true
            const participantCalendars = this.calendars.filter(calendar => calendar.practitioner_user?.id === participant.id);
            const organizer = this.meeting.organizer?.id || this.user.id;
            // check which one of the above calendars the participant has edit access to, and then check if the organizer has access to it
            const canEditParticipantCalendars = participantCalendars.filter(calendar => {
                const canParticipantSee = this.canUserSeeCalendar(participant, calendar.id);
                const canOrganizerEdit = this.canUserEditCalendar(organizer, calendar.id);
                return canParticipantSee && (canOrganizerEdit || this.isOwner);
            });
            // if the organizer has access to any of the above calendars => true
            if (canEditParticipantCalendars.length > 0) return true

            // if the participant has access to the calendar where the meeting is being created, => true
            const hasAccessToCalendarWithMeeting = this.canUserSeeCalendar(participant, this.meeting.calendarId)
            if (hasAccessToCalendarWithMeeting) return true;

            // no access to any of the above => false
            return false;
        },

        setSelectedParticipants(meeting) {
            if (!meeting?.participants?.length) {
                return;
            }

            this.meeting.participants = this.participantOptions
                .filter(participant => {
                    return meeting.participants.some(selectedParticipant => selectedParticipant.id === participant.id);
                })
                .map(participant => {
                    const accessGranted = this.setMeetingAccess(participant);
                    return { ...participant, canSeeMeeting: accessGranted };
                })
        },

        removeParticipant(participant) {
            this.meeting.participants = this.meeting?.participants
                .filter(selectedParticipant => selectedParticipant.id !== participant.id);
        },

        resetForm() {
            this.meeting = {
                title: '',
                startDate: moment().format('DD-MM-YYYY'),
                startTime: moment().startOf('hour').format('HH:mm'),
                endDate: moment().format('DD-MM-YYYY'),
                endTime: moment().add(1, 'hours').startOf('hour').format('HH:mm'),
                calendarId: this.$store.getters['calendars/getDefaultCalendar']().id,
                description: null,
                color: null,
                participants: [],
            };
            this.selectedParticipant = null;
            this.repeatInterval = 1;
            this.repeatUntilDate = null;
            this.showRepeatOptions = false;
            this.applyRecurringActionTo = null;
            this.calendarsWithExistingEntries = [];
        },

        async grantCalendarAccess(userId) {
            // todo: 1. check if the user has edit access to apps
            // todo: 2. If yes, check if the user has edit access to the meeting calendar
            try {
                this.grantAccessLoader = true;
                this.grantingAccessParticipantId = userId;
                await http.post(`/app/usermanagement/${userId}/${this.meeting.calendarId}`);
                await this.$store.dispatch('practitioners/reloadPractitioners');
                this.meeting.participants.find(participant => participant.id === userId).canSeeMeeting = true;
            } catch (e) {
                console.error(e);
            } finally {
                this.grantAccessLoader = false;
                this.grantingAccessParticipantId = null;
            }
        },

        alertMessage({ team_meetings }) {
            const message = this.trans('apps.team_meetings.multiple_meetings_warning', { count: team_meetings.length });

            const dates = team_meetings.map(meeting => {
                return `<p>${meeting}</p>`;
            }).join('');

            const confirmation = this.trans('apps.team_meetings.confirm_create');

            return `
                <div class="mb-5">${message}</div>
                <div class="mb-5">${dates}</div>
                <div>${confirmation}</div>
            `;
        },

        getExistingEntriesRequest (newValue, oldValue) {
            if (newValue && newValue !== oldValue) {
                this.getExistingEntries();
                this.getCalendarsWithExistingEntries();
            }
        },

        getCalendarsWithExistingEntries: debounce(async function () {
            // if this is a recurring meeting, or in edit mode, don't check for existing entries
            if (this.showRepeatOptions || this.editMode || !this.meeting.participants.length) {
                return;
            }

            let start = this.getSelectedStartDateTime();
            let end = this.getSelectedEndDateTime();

            if (!start || !start.isValid() || !end || !end.isValid()) {
                return;
            }

            // Cancel the previous request if it's still running
            if(this.cancelTokenSource){
                this.cancelTokenSource.cancel('Operation canceled by the user.');
            }
            // Create a new cancel token for the new request
            const CancelToken = axios.CancelToken;
            this.cancelTokenSource = CancelToken.source();

            // Get the calendars of the participants
            const calendars = this.meeting.participants
                .map(participant => this.practitionerCalendarMap[participant.id])
                .filter(calendar => calendar && calendar !== this.meeting.calendarId);

            // now get the existing entries for the calendars
            try {
                this.entriesLoading = true;
                const { data } = await http.get('/app/team-meetings/meetings/existing-entries', {
                    cancelToken: this.cancelTokenSource.token,
                    params: {
                        calendars,
                        start: `${this.meeting.startDate} ${this.meeting.startTime}`,
                        end: `${this.meeting.endDate} ${this.meeting.endTime}`,
                    }
                });

                // the response verifies if the selected calendars have existing entries or not
                this.calendarsWithExistingEntries = data.calendars || [];
            } finally {
                this.entriesLoading = false;
            }
        }, 150),

        // overriding the mixin method
        getSelectedStartDateTime() {
            if (!this.meeting.startDate || !this.meeting.startTime) {
                return null;
            }
            return moment(`${this.meeting.startDate} ${this.meeting.startTime}`, 'DD-MM-YYYY HH:mm');
        },

        // overriding the mixin method
        getSelectedEndDateTime() {
            if (!this.meeting.endDate || !this.meeting.endTime) {
                return null;
            }
            return moment(`${this.meeting.endDate} ${this.meeting.endTime}`, 'DD-MM-YYYY HH:mm');
        },

        // overriding the mixin method
        getSelectedCalendarId() {
            return this.meeting.calendarId;
        },
    },
};
</script>
